import React, { useState, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import {
  Volume2,
  Upload,
  Mic,
  Loader2,
  AlertCircle,
  Play,
  Pause,
  Download,
  Trash2,
  Check,
} from 'lucide-react'

export function QwenVoiceCloneTool() {
  const { user } = useAuth()
  const [text, setText] = useState('')
  const [referenceFile, setReferenceFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3']
      if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav)$/i)) {
        setError('Please select a valid audio file (MP3 or WAV)')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB')
        return
      }
      setReferenceFile(file)
      setAudioUrl(null)
      setError(null)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && (file.type.startsWith('audio/') || file.name.match(/\.(mp3|wav)$/i))) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB')
        return
      }
      setReferenceFile(file)
      setAudioUrl(null)
      setError(null)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const clearReferenceFile = () => {
    setReferenceFile(null)
    setError(null)
  }

  const generateClonedSpeech = async () => {
    if (!text.trim() || !referenceFile || !user) return

    setUploading(true)
    setGenerating(true)
    setError(null)
    setAudioUrl(null)

    try {
      // Step 1: Upload reference audio to Supabase storage
      const fileName = `${user.id}/${Date.now()}-${referenceFile.name}`
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('generated-audio')
        .upload(fileName, referenceFile, {
          contentType: referenceFile.type,
          upsert: false,
        })

      if (uploadError) throw uploadError

      setUploading(false)

      // Step 2: Get public URL for the uploaded file
      const { data: publicData } = supabase.storage
        .from('generated-audio')
        .getPublicUrl(fileName)

      if (!publicData.publicUrl) {
        throw new Error('Failed to get public URL for reference audio')
      }

      const referenceAudioUrl = publicData.publicUrl

      // Step 3: Call the generate-speech edge function with reference audio
      const { data, error: functionError } = await supabase.functions.invoke('generate-speech', {
        body: {
          text: text.trim(),
          reference_audio_url: referenceAudioUrl,
        },
      })

      if (functionError) throw functionError

      if (data.error) {
        throw new Error(data.message || data.error)
      }

      // Step 4: Set the generated audio URL
      if (data.audio_url) {
        setAudioUrl(data.audio_url)
      } else {
        throw new Error('No audio URL returned from the service')
      }
    } catch (err: any) {
      console.error('Voice cloning error:', err)
      setError(err.message || 'An error occurred during voice cloning')
    } finally {
      setUploading(false)
      setGenerating(false)
    }
  }

  const togglePlayPause = () => {
    if (!audioRef.current) return

    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current.play()
      setIsPlaying(true)
    }
  }

  const downloadAudio = () => {
    if (!audioUrl) return

    const a = document.createElement('a')
    a.href = audioUrl
    a.download = `qwen-cloned-voice-${Date.now()}.mp3`
    a.click()
  }

  const handleAudioEnded = () => {
    setIsPlaying(false)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Qwen Voice Clone</h1>
        <p className="text-slate-600">
          Clone a voice from a short audio sample and generate speech with that voice
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Input */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Mic className="h-5 w-5" />
            Voice Reference & Text
          </h2>

          <div className="space-y-6">
            {/* Voice Reference Upload */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Voice Reference Audio
              </label>

              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600 mb-1 text-sm">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-slate-400">MP3 or WAV, max 10MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/mpeg,audio/wav,audio/mp3,.mp3,.wav"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {referenceFile && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-slate-800">
                          {referenceFile.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatFileSize(referenceFile.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={clearReferenceFile}
                      className="text-slate-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Text Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Script to Synthesize
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type the text you want to be spoken in the cloned voice..."
                className="w-full h-32 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                maxLength={5000}
              />
              <p className="text-xs text-slate-500 mt-1">
                {text.length} / 5000 characters
              </p>
            </div>

            <Button
              onClick={generateClonedSpeech}
              disabled={!text.trim() || !referenceFile || generating}
              className="w-full"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {uploading ? 'Uploading Reference...' : 'Cloning Voice...'}
                </>
              ) : (
                <>
                  <Volume2 className="h-4 w-4 mr-2" />
                  Generate Cloned Voice
                </>
              )}
            </Button>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Error</p>
                  <p className="text-sm">{error}</p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Right Panel - Audio Player */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Volume2 className="h-5 w-5" />
            Cloned Voice Audio
          </h2>

          {audioUrl ? (
            <div className="space-y-4">
              {/* Audio element (hidden) */}
              <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={handleAudioEnded}
                className="hidden"
              />

              {/* Custom audio player UI */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={togglePlayPause}
                    className="h-16 w-16 rounded-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center transition-colors shadow-lg"
                  >
                    {isPlaying ? (
                      <Pause className="h-8 w-8" />
                    ) : (
                      <Play className="h-8 w-8 ml-1" />
                    )}
                  </button>
                </div>

                <div className="mt-4 text-center">
                  <p className="text-sm text-slate-600">
                    {isPlaying ? 'Playing cloned voice...' : 'Ready to play'}
                  </p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button onClick={downloadAudio} variant="outline" className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Download MP3
                </Button>
              </div>

              {/* Info */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-600">
                  <strong>Voice cloning complete!</strong> Your audio has been synthesized
                  with the reference voice.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Mic className="h-16 w-16 mb-4" />
              <p className="text-center px-4">
                Upload a voice reference and enter text to clone the voice
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Tips */}
      <Card className="p-6 bg-purple-50 border-purple-200">
        <h3 className="font-semibold text-slate-800 mb-2">Tips for Best Voice Cloning</h3>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>• Use a clear 5-10 second audio sample with minimal background noise</li>
          <li>• The reference audio should contain the voice you want to clone</li>
          <li>• Supported formats: MP3, WAV (max 10MB)</li>
          <li>• Better reference quality = more accurate voice cloning</li>
          <li>• Use proper punctuation in your text for natural intonation</li>
        </ul>
      </Card>
    </div>
  )
}
